import Analytics from '../analytics';
import Environment from '../environment';
import '../lib/webOSTV';
import '../lib/webOSTV-dev';
import LinkHandler from '../linkHandler';
import { getLogger } from '../logging/logger';
import AudioManager from '../manager/audioManager';
import { addReadyPromise } from '../util/utilities';

/**
 * @typedef {object} LaunchParams
 * @property {string} [contentId]
 * @property {Record<string, any>} [params]
 *
 * @typedef {object} WebOSLaunchOptions
 * @property {string} id - The app ID to launch
 * @property {Record<string, any>} [params] - Parameters to pass to the launched app
 * @property {(response: any) => void} [onSuccess] - Success callback
 * @property {(error: any) => void} [onFailure] - Failure callback
 *
 * @typedef {object} DeviceInfo
 * @property {string} modelName - TV model name
 * @property {string} version - OS version
 * @property {string} versionMajor - Major version number
 * @property {string} versionMinor - Minor version number
 * @property {string} versionDot - Dot version number
 * @property {string} sdkVersion - SDK version
 * @property {string} screenWidth - Screen width in pixels
 * @property {string} screenHeight - Screen height in pixels
 * @property {boolean} [uhd] - UHD support flag
 * @property {boolean} [uhd8K] - 8K UHD support flag
 * @property {boolean} [oled] - OLED display flag
 * @property {string} [ddrSize] - DDR size (e.g., "2GB")
 * @property {boolean} [hdr10] - HDR10 support flag
 * @property {boolean} [dolbyVision] - Dolby Vision support flag
 * @property {boolean} [dolbyAtmos] - Dolby Atmos support flag
 * @property {string} [brandName] - Brand name (e.g., "LG")
 * @property {string} [manufacturer] - Manufacturer name
 * @property {string} [mainboardMaker] - Mainboard maker name
 * @property {string} [platformBizType] - Platform business type
 * @property {boolean} [tuner] - Tuner availability flag
 *
 * @typedef {(params: LaunchParams) => void} LaunchHandler
 * @typedef {(info: DeviceInfo) => void} DeviceInfoCallback
 * @typedef {(info: any) => void} AppInfoCallback
 */

const logger = getLogger('WebOS');

const { isReady, onReadyReject, onReadyResolve } = addReadyPromise();

class WebOS extends LinkHandler {
	constructor() {
		super();
		logger.log('[Constructor]');

		this.isReady = isReady;

		this.launchParams = null;
		this.deviceInfo = null;
		this.launchCallback = null;
	}

	/**
	 * Regular expression pattern for matching WebOS deeplink URIs
	 * @returns {RegExp} Pattern matching "webos://appId" or "webos://appId?param=value"
	 */
	get linkPattern() {
		return /^webos:\/\/([\w.]+)(\?(.*))?/;
	}

	async initialize() {
		logger.log(`[${this.initialize.name}]`);

		// @ts-ignore
		if (!window.webOS) {
			const error = new Error('webOS object not available');
			logger.error(`[${this.initialize.name}] Error:`, error);
			await Analytics.sendEvent('WebOSInit-Error', { error });
			onReadyReject(error);
			return;
		}

		try {
			await new Promise((resolve) => {
				// @ts-ignore
				window.webOS.deviceInfo((/** @type {DeviceInfo} */deviceInfo) => {
					this.deviceInfo = deviceInfo;
					resolve(deviceInfo);
				});
			}).catch(error => {
				logger.error(`[${this.initialize.name}] deviceInfo Error:`, error);
			});

			await new Promise((resolve) => {
				// @ts-ignore
				window.webOS.fetchAppInfo((/** @type {any} */appInfo) => {
					this.launchParams = appInfo;
					resolve(appInfo);
				});
			}).catch(error => {
				logger.error(`[${this.initialize.name}] fetchAppInfo Error:`, error);
			});

			// Set up lifecycle event handlers
			document.addEventListener('webOSLaunch', this.onLaunch.bind(this));
			document.addEventListener('webOSRelaunch', this.onRelaunch.bind(this));

			onReadyResolve(this);
		} catch (error) {
			logger.error(`[${this.initialize.name}] Error:`, error);
			await Analytics.sendEvent('WebOSInit-Error', { error });
			onReadyReject(/** @type {Error} */(error));
		}
	}

	/**
	 * @param {Event} event
	 */
	async onLaunch(event) {
		// @ts-ignore
		const launchEvent = /** @type {CustomEvent} */ (event);
		logger.log(`[${this.onLaunch.name}]`, { launchEvent });

		this.launchParams = launchEvent.detail;

		if (this.launchCallback) {
			this.launchCallback(launchEvent.detail);
		}

		await Analytics.isReady;
		await Analytics.sendEvent('WebOSLaunch', { params: launchEvent?.detail });
	}

	/**
	 * @param {Event} event
	 */
	async onRelaunch(event) {
		// @ts-ignore
		const relaunchEvent = /** @type {CustomEvent} */ (event);
		logger.log(`[${this.onRelaunch.name}]`, { relaunchEvent });

		this.launchParams = relaunchEvent.detail;

		if (this.launchCallback) {
			this.launchCallback(relaunchEvent.detail);
		}

		AudioManager.resumeBackgroundMusic();
		await Analytics.isReady;
		await Analytics.sendEvent('WebOSRelaunch', { params: relaunchEvent?.detail });
	}

	/**
	 * Register a callback for launch/relaunch events
	 * @param {LaunchHandler} callback
	 */
	onLaunchParams(callback) {
		logger.log(`[${this.onLaunchParams.name}]`);

		this.launchCallback = callback;

		// If already launched, call immediately with current params
		if (this.launchParams) {
			callback(this.launchParams);
		}
	}

	/**
	 * @returns {DeviceInfo | null}
	 */
	getDeviceInfo() {
		logger.log(`[${this.getDeviceInfo.name}]`);

		return this.deviceInfo;
	}

	/**
	 * @returns {LaunchParams | null}
	 */
	getLaunchParams() {
		logger.log(`[${this.getLaunchParams.name}]`);

		return this.launchParams;
	}

	/**
	 * Launch another webOS application with parameters
	 * @param {WebOSLaunchOptions} options Launch options containing app ID and params
	 * @returns {Promise<any>}
	 */
	async launch(options) {
		logger.log(`[${this.launch.name}]`, { options });

		// @ts-ignore
		if (!window.webOSDev?.launch) {
			throw new Error('webOSDev.launch not available');
		}

		return new Promise((resolve, reject) => {
			// @ts-ignore
			window.webOSDev.launch({
				...options,
				onSuccess: (/** @type {any} */ response) => {
					logger.debug(`[${this.launch.name} Success]`, { response });
					if (options.onSuccess) {
						options.onSuccess(response);
					}
					resolve(response);
				},
				onFailure: (/** @type {any} */ error) => {
					logger.error(`[${this.launch.name} Error]`, { error });
					if (options.onFailure) {
						options.onFailure(error);
					}
					reject(error);
				},
			});
		});
	}

	/**
	 * Handle webOS deeplink URIs (webos://appId or webos://appId?params)
	 * @param {string} uri The deeplink URI to process
	 */
	async handleLink(uri) {
		logger.log(`[${this.handleLink.name}]`, { uri });

		if (!this.linkPattern.test(uri)) {
			logger.warn(`Invalid webOS deeplink format: ${uri}`);
			return;
		}

		const [, appId, , queryString] = this.linkPattern.exec(uri) || [];
		if (!appId) {
			logger.warn('No app ID found in webOS deeplink');
			return;
		}

		// Parse query string into params object
		/** @type {Record<string, string>} */
		const params = {};
		if (queryString) {
			const urlParams = new URLSearchParams(queryString);
			urlParams.forEach((value, key) => {
				params[key] = value;
			});
		}

		try {
			await Analytics.sendEvent('WebOSDeepLink', { appId, params, uri });
			await this.launch({ id: appId, params });
		} catch (error) {
			logger.error(`[${this.handleLink.name} Error]`, { error });
			await Analytics.sendEvent('WebOSDeepLink-Error', {
				appId,
				params,
				uri,
				error: error instanceof Error ? error.message : String(error),
			});
		}
	}

	/**
	 * Close the application
	 */
	async closeApp() {
		logger.log(`[${this.closeApp.name}]`);

		await Analytics.sendEvent('WebOSClose');
		window.close();
	}

	/**
	 * Request system info from webOS
	 * @param {string} keys Comma-separated list of keys to request
	 * @returns {Promise<any>}
	 */
	async systemInfo(keys) {
		logger.log(`[${this.systemInfo.name}]`, { keys });

		// @ts-ignore
		if (!window.webOS?.systemInfo) {
			throw new Error('webOS.systemInfo not available');
		}

		return new Promise((resolve, reject) => {
			// @ts-ignore
			window.webOS.systemInfo(keys, resolve, reject);
		});
	}

	/**
	 * Call a Luna service
	 * @param {string} uri Service URI
	 * @param {string} method Service method
	 * @param {Record<string, any>} params Service parameters
	 * @returns {Promise<any>}
	 */
	async service(uri, method, params = {}) {
		logger.log(`[${this.service.name}]`, { uri, method, params });

		// @ts-ignore
		if (!window.webOS?.service?.request) {
			throw new Error('webOS.service not available');
		}

		return new Promise((resolve, reject) => {
			// @ts-ignore
			window.webOS.service.request(uri, {
				method,
				parameters: params,
				onSuccess: (/** @type {any} */ response) => {
					logger.debug(`[${this.service.name} Success]`, { response });
					resolve(response);
				},
				onFailure: (/** @type {any} */ error) => {
					logger.error(`[${this.service.name} Error]`, { error });
					reject(error);
				},
			});
		});
	}

	/**
	 * Get webOS TV API version
	 * @returns {string | null}
	 */
	getAPIVersion() {
		return this.deviceInfo?.sdkVersion || null;
	}

	/**
	 * Check if device supports UHD
	 * @returns {boolean}
	 */
	isUHDSupported() {
		return this.deviceInfo?.uhd === true;
	}
}

const webOS = new WebOS();

// Initialize asynchronously outside the constructor to satisfy linting rules (S7059)
if (Environment.isWebOS() && !Environment.isInHub()) {
	webOS.initialize();
}

export default webOS;
